import type { EmptyObject } from 'type-fest';
import { isBlankValue } from './isBlankValue';

export function isEmptyObject(value: object | null | undefined, depth = 0): value is EmptyObject {
	// Simplest case
	if (value === null || value === undefined) {
		return true;
	}

	// Limit how deep we inspect objects
	if (depth > 20) {
		return false;
	}

	// Look at object properties, since keys may not be
	const properties = Object.getOwnPropertyNames(value);
	if (properties.length) {
		const objectWithProperties = value as { [key: string]: unknown };
		for (const property of properties) {
			const propertyValue = objectWithProperties[property];
			const notEmpty = isNotEmpty(propertyValue, depth + 1);
			if (notEmpty) {
				return false;
			}
		}
	}

	// If we didn't find anything on the object itself, check the prototype
	const valuePrototype = Object.getPrototypeOf(value);

	// Follow the prototype chain (if there is one)
	if (valuePrototype) {
		// If the prototype is not the base Object prototype, check it too
		if (valuePrototype !== Object.prototype) {
			return isEmptyObject(valuePrototype, depth + 1);
		}
	}

	// If we get this far, assume the object is empty
	return true;
}

type EmptyArray = [] | undefined | null;
type _NonEmptyArray<T> = [T, ...T[]];

export function isEmptyArray<T>(values: T[] | undefined | null, depth = 0): values is EmptyArray {
	// Not empty if at least one value in the array is not empty
	return !values?.some((item) => isNotEmpty(item, depth + 1));
}

export function isEmpty(value: unknown, depth = 0): boolean {
	// For strings, trim before checking if they're empty
	if (isBlankValue(value)) {
		return true;
	}

	// Don't consider functions as empty
	if (typeof value === 'function') {
		return false;
	}

	// Simple check for arrays first
	if (Array.isArray(value)) {
		if (value.length === 0) {
			return true;
		}
	}

	// Limit how deep we inspect objects
	if (depth > 20) {
		try {
			throw new Error(`isEmpty: depth limit exceeded, object type: [${typeof value}]`);
		} catch (error) {
			console.debug(`isEmpty: depth limit exceeded, object type: [${typeof value}]`, error);
		}
		return false;
	}

	// Longer check for arrays after
	if (Array.isArray(value)) {
		return isEmptyArray(value, depth + 1);
	}

	// Empty object
	if (value instanceof Object) {
		return isEmptyObject(value, depth + 1);
	}

	return false;
}

export function isNotEmpty(value: unknown, depth = 0): boolean {
	return !isEmpty(value, depth);
}
